import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectAppContextDwsmConfig, selectUserLanguage } from '../../core/state/core.selectors';
import { DWSMUtils } from '../dwsm-utils';
import { OverviewTMORequestParams } from '../models/OverviewTMORequestParams';
import { DWSMState, featureKey } from './dwsm.state';
import { VariantDTO } from '../models/VariantDTO';

export const selectDWSMState = createFeatureSelector<DWSMState>(featureKey);

export const selectUploadOverview = createSelector(selectDWSMState, state => state?.uploadOverview);

export const selectTimeOfActivity = createSelector(selectUploadOverview, state => state?.timeOfActivity);

export const selectProcessFrequency = createSelector(selectUploadOverview, state => state?.processFrequency);

export const selectFileNames = createSelector(selectUploadOverview, state => state?.fileNames);

export const selectNumberOfOutliers = createSelector(selectUploadOverview, state => state?.numberOfOutliers);

export const selectMM01Outliers = createSelector(selectNumberOfOutliers, state => state?.mm01);

export const selectMM02Outliers = createSelector(selectNumberOfOutliers, state => state?.mm02);

export const selectMM03Outliers = createSelector(selectNumberOfOutliers, state => state?.mm03);

export const selectMM06Outliers = createSelector(selectNumberOfOutliers, state => state?.mm06);

export const selectGloveOutliers = createSelector(selectNumberOfOutliers, state => state?.glove);

export const selectTotalOutliers = createSelector(selectNumberOfOutliers, state => state?.total);

export const selectShowGloveOutliers = createSelector(selectFileNames, state => state?.some(fileName => fileName.endsWith('.txt')));

export const selectShowDWSMPopup = createSelector(selectDWSMState, state => state?.showDWSMPopup);

export const selectIsUploading = createSelector(selectDWSMState, state => state?.isUploading);

export const selectIsToCancelUpload = createSelector(selectDWSMState, state => state?.cancelUpload);

export const selectDwsmJob = createSelector(selectDWSMState, state => state?.dwsmJob);

export const selectDWSMStatus = createSelector(selectDwsmJob, state => state?.status);

export const selectDwsmJobId = createSelector(selectDwsmJob, state => state?.id);

export const selectIsRedirectingToPageOnPopUp = createSelector(selectDWSMState, state => state?.isRedirectingToPageOnPopUp);

export const selectDwsmConfig = createSelector(selectDWSMState, state => state?.dwsmConfig);

export const selectActiveDwsmConfig = createSelector(
    selectAppContextDwsmConfig,
    selectDwsmConfig,
    (selectAppContextDwsmConfig, dwsmConfig) => dwsmConfig ?? selectAppContextDwsmConfig
);

export const selectDwsmIsLoading = createSelector(selectDWSMState, state => state?.dwsmIsLoading);

export const selectDwsmGlobalIsLoading = createSelector(selectDWSMState, state => state?.dwsmGlobalIsLoading);

export const selectAdvancedSettingsIsLoading = createSelector(selectDWSMState, state => state?.advancedSettingsIsLoading);

export const selectDwsmOverviewIsLoading = createSelector(
    selectDwsmIsLoading,
    selectAdvancedSettingsIsLoading,
    (dwsmIsLoading, advancedSettingsIsLoading) => dwsmIsLoading || advancedSettingsIsLoading
);

export const selectXsensCalculations = createSelector(selectDWSMState, state => state?.calculation);

export const selectXsensDataTable = createSelector(selectXsensCalculations, calculation => calculation?.xsens);

export const selectForceCasesData = createSelector(selectXsensCalculations, calculation => calculation?.glove);

export const selectXsensDataMM01 = createSelector(selectXsensDataTable, xsensDataTable => xsensDataTable?.dataMM01);

export const selectXsensDataMM02 = createSelector(selectXsensDataTable, xsensDataTable => xsensDataTable?.dataMM02);

export const selectXsensDataMM03 = createSelector(selectXsensDataTable, xsensDataTable => xsensDataTable?.dataMM03);

export const selectXsensDataMM06 = createSelector(selectXsensDataTable, xsensDataTable => xsensDataTable?.dataMM06);

export const selectOverviewTmo = createSelector(selectDWSMState, state => state?.overviewTmo);

export const selectOverviewTmoVariantsWithDescriptions = createSelector(selectOverviewTmo, selectUserLanguage, (overviewTmo, language) => {
    return overviewTmo?.variants?.map(variant => ({
        ...variant,
        activeMarkers: variant.activeMarkers?.map(marker => ({
            ...marker,
            tvg: marker.tvg
                ? {
                      ...marker.tvg,
                      description: language ? DWSMUtils.getTvgDescription(marker.tvg, language) : marker.tvg.description
                  }
                : null
        }))
    }));
});

export const selectOverviewTmoAllVerified = createSelector(selectOverviewTmo, state => state?.allVerified);

export const selectVariants = createSelector(selectDWSMState, state => state?.overviewTmo?.variants);

export const selectUniqueTvgIds = createSelector(selectVariants, variants => {
    const tvgIds = new Set<string>();

    variants?.forEach(variant => {
        variant.activeMarkers?.forEach(marker => {
            if (marker.tvg?.id && marker.tvg.id !== 'NON PRODUCTIVE TVG') {
                tvgIds.add(marker.tvg.id);
            }
        });
    });

    return Array.from(tvgIds);
});

export const selectUnverifiedTvgs = createSelector(selectVariants, variants => {
    const tvgIds = new Set<string>();

    variants?.forEach(variant => {
        variant.activeMarkers?.forEach(marker => {
            if (marker.tvg?.id && marker.tvg.id !== 'NON PRODUCTIVE TVG' && !marker.tvg.verified) {
                tvgIds.add(marker.tvg.id);
            }
        });
    });

    return Array.from(tvgIds);
});

export const selectActiveVariantUuid = createSelector(selectDWSMState, state => state?.activeVariantUuid);

export const selectActiveVariant = createSelector(selectOverviewTmo, selectActiveVariantUuid, (overviewTmo, activeVariantUUID) =>
    overviewTmo?.variants.find(item => item.variantUUID == activeVariantUUID)
);

export const selectActiveVariantRecordings = createSelector(selectActiveVariant, activeVariant => {
    return activeVariant?.fileTable?.map(item => DWSMUtils.buildDwsmRecording(item));
});

export const selectTimeOfActivityTmo = createSelector(selectOverviewTmo, overviewTmo => overviewTmo?.timeOfActivity);

export const selectProcessFrequencyTmo = createSelector(selectOverviewTmo, overviewTmo => overviewTmo?.processFrequency);

export const selectMuaShortDescription = createSelector(selectOverviewTmo, overviewTmo => overviewTmo?.muaShortDescription ?? 'N/A');

export const selectOverviewTmoFileNames = createSelector(selectOverviewTmo, overviewTmo => overviewTmo?.fileNames?.join(', ') ?? '');

export const selectOverviewTmoActiveVariantTvgList = createSelector(
    selectOverviewTmo,
    selectActiveVariantUuid,
    selectUserLanguage,
    (overviewTmo, activeVariantUUID, language) => {
        return { tvgTable: overviewTmo?.variants.find(item => item.variantUUID == activeVariantUUID)?.tvgTable, language: language };
    }
);

export const selectOverviewTmoActiveVariantMarkers = createSelector(
    selectOverviewTmo,
    selectActiveVariantUuid,
    (overviewTmo, activeVariantUUID) =>
        overviewTmo?.variants?.find(item => item.variantUUID == activeVariantUUID)?.fileTable?.find(item => item.active)?.markersInfo
);

export const selectOverviewTmoActiveVariantActiveMarkers = createSelector(
    selectOverviewTmo,
    selectActiveVariantUuid,
    selectUserLanguage,
    (overviewTmo, activeVariantUUID, language) => {
        const activeMarkers = overviewTmo?.variants.find(item => item.variantUUID == activeVariantUUID)?.activeMarkers;

        if (!activeMarkers || !language) {
            return activeMarkers;
        }

        return activeMarkers.map(marker => {
            if (!marker.tvg) {
                return marker;
            }

            return {
                ...marker,
                tvg: {
                    ...marker.tvg,
                    description: DWSMUtils.getTvgDescription(marker.tvg, language)
                }
            };
        });
    }
);

export const selectTmoUpdateRequestParams = createSelector(
    selectDwsmJobId,
    selectOverviewTmo,
    selectActiveVariantUuid,
    (dwsmJobId, overviewTmo, activeVariantUuid) => {
        return {
            dwsmJobId: dwsmJobId,
            overviewTmo: { ...overviewTmo },
            variantUuid: activeVariantUuid
        } as OverviewTMORequestParams;
    }
);

export const selectMaxDurationToScaleChart = createSelector(selectActiveVariant, activeVariant => activeVariant?.maxDurationToScaleChart);

export const selectMarkersChartOptions = createSelector(selectMaxDurationToScaleChart, maxDurationToScaleChart => {
    return {
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: false
            }
        },
        scales: {
            x: {
                stacked: true,
                display: false,
                max: maxDurationToScaleChart
            },
            y: {
                beginAtZero: true,
                stacked: true,
                display: false
            }
        },
        animation: {
            duration: 0
        }
    };
});

export const selectDwsmDashboardEntries = createSelector(selectDWSMState, state => state.dwsmDashboardEntries);

export const selectDwsmLiteDashboardEntries = createSelector(selectDWSMState, state => state.dwsmLiteDashboardEntries);

export const selectDwsmTmoDashboardEntries = createSelector(selectDWSMState, state => state.dwsmTmoDashboardEntries);

export const selectIsDwsmJobsLoading = createSelector(selectDWSMState, state => state.isDwsmDashboardLoading);

export const selectIsDwsmLiteJobsLoading = createSelector(selectDWSMState, state => state.isDwsmLiteDashboardLoading);

export const selectIsDwsmTmoJobsLoading = createSelector(selectDWSMState, state => state.isDwsmTmoDashboardLoading);

export const selectShowForceCaseTypePopup = createSelector(selectDWSMState, state => state.showForceCaseTypePopup);

export const selectAreAllDistinctFilesActive = createSelector(selectVariants, (variants: VariantDTO[] | undefined): boolean => {
    if (!variants) {
        return false;
    }

    const fileNames = new Set<string>();
    const activeFileNames = new Set<string>();

    variants.forEach(variant => {
        variant.fileTable?.forEach(file => {
            fileNames.add(file.name);
            if (file.active) {
                activeFileNames.add(file.name);
            }
        });
    });
    return fileNames.size === activeFileNames.size;
});

export const selectCurrentTvgById = (tvgId: string) =>
    createSelector(selectOverviewTmo, selectActiveVariantUuid, (overviewTmo, activeVariantUUID) => {
        return overviewTmo?.variants.find(item => item.variantUUID == activeVariantUUID)?.activeMarkers.find(marker => marker.tvg && marker.tvg.id === tvgId)
            ?.tvg;
    });

export const selectUploadedDWSMLiteFile = createSelector(selectDWSMState, state => state?.uploadedDWSMLiteFile);

export const selectDropdownTvgDTOs = createSelector(selectDWSMState, selectUserLanguage, (state, language) =>
    state?.muaTVGs?.map(tvg => ({
        ...tvg,
        description: language ? DWSMUtils.getTvgDescription(tvg, language) : tvg.description
    }))
);

export const selectIsStartingDwsmLiteTmoProcess = createSelector(selectDWSMState, state => state?.isStartingDwsmLiteTmoProcess);

export const selectIsWaitingForDwsmLiteTmoProcess = createSelector(selectDWSMState, state => state?.isWaitingForDwsmLiteTmoProcess);
